import { API_BASE } from './client.js'
import { ApiError } from '../types/api.js'

function token(): string | null {
  return localStorage.getItem('mf_access')
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const t = token()
  if (t) headers['Authorization'] = `Bearer ${t}`
  return headers
}

async function toApiError(res: Response, fallback: string): Promise<ApiError> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    // non-JSON error response
  }
  const errBody =
    (body as { error?: { code: string; message: string; details?: Record<string, unknown> } } | null)
      ?.error ?? { code: 'UNKNOWN', message: fallback }
  return new ApiError(res.status, errBody)
}

// ─── Product availability ─────────────────────────────────────────

export interface ProductAvailabilityItem {
  locationId: string
  locationName: string
  isAvailable: boolean
}

export interface SetProductAvailabilityData {
  availability: { locationId: string; isAvailable: boolean }[]
}

export async function getProductAvailability(
  organizationId: string,
  productId: string,
): Promise<ProductAvailabilityItem[]> {
  const res = await fetch(
    `${API_BASE}/organizations/${organizationId}/products/${productId}/availability`,
    { headers: authHeaders() },
  )
  if (!res.ok) throw await toApiError(res, 'Failed to load product availability')
  const b = await res.json()
  return b.data
}

export async function updateProductAvailability(
  organizationId: string,
  productId: string,
  data: SetProductAvailabilityData,
): Promise<ProductAvailabilityItem[]> {
  const res = await fetch(
    `${API_BASE}/organizations/${organizationId}/products/${productId}/availability`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(data),
    },
  )
  if (!res.ok) throw await toApiError(res, 'Failed to update product availability')
  const b = await res.json()
  return b.data
}

// ─── Menu availability ────────────────────────────────────────────

export interface MenuAvailabilityItem {
  locationId: string
  locationName: string
  isActive: boolean
  availableFrom: string | null
  availableUntil: string | null
}

export interface SetMenuAvailabilityData {
  availability: {
    locationId: string
    isActive: boolean
    availableFrom?: string | null
    availableUntil?: string | null
  }[]
}

export async function getMenuAvailability(
  organizationId: string,
  menuId: string,
): Promise<MenuAvailabilityItem[]> {
  const res = await fetch(
    `${API_BASE}/organizations/${organizationId}/menus/${menuId}/availability`,
    { headers: authHeaders() },
  )
  if (!res.ok) throw await toApiError(res, 'Failed to load menu availability')
  const b = await res.json()
  return b.data
}

export async function updateMenuAvailability(
  organizationId: string,
  menuId: string,
  data: SetMenuAvailabilityData,
): Promise<MenuAvailabilityItem[]> {
  const res = await fetch(
    `${API_BASE}/organizations/${organizationId}/menus/${menuId}/availability`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(data),
    },
  )
  if (!res.ok) throw await toApiError(res, 'Failed to update menu availability')
  const b = await res.json()
  return b.data
}